import { control, curve, display, readout, type PhysicsDomain } from './domainTypes';

export const thermal: PhysicsDomain = {
  id: 'thermal',
  name: 'Heat & Thermal Lab',
  hint: 'Compare temperature rise, phase change and energy transfer using your own values.',
  experiments: [
    {
      id: 'specific-heat',
      name: 'Heating a material',
      question: 'Does the same energy heat every material by the same amount?',
      controls: [
        control('mass', 'Mass', 'kg', 0.1, 5, 1, 0.1),
        control('capacity', 'Specific heat capacity', 'J/(kg K)', 100, 5000, 4200, 100),
        control('energy', 'Energy supplied', 'J', 0, 50000, 4200, 100),
        control('initial', 'Initial temperature', '°C', 0, 50, 20),
      ],
      examples: [
        {
          id: 'water',
          name: 'Water-like material',
          values: {},
          note: 'Classroom heat capacity: 4200 J/(kg K).',
        },
        {
          id: 'metal',
          name: 'Metal-like material',
          values: { capacity: 900 },
          note: 'A lower heat capacity gives a larger temperature rise.',
        },
        {
          id: 'double-mass',
          name: 'Twice the mass',
          values: { mass: 2 },
          note: 'Twice the mass shares the same supplied energy.',
        },
      ],
      assumptions:
        'Constant heat capacity, no heat loss and no phase change. Material presets are rounded classroom values. If a real sample reaches a phase boundary, use the phase-change experiment instead.',
      source:
        'https://openstax.org/books/university-physics-volume-2/pages/1-4-heat-transfer-specific-heat-and-calorimetry',
      calculate: ({ mass, capacity, energy, initial }) => {
        const rise = energy / (mass * capacity);
        return {
          readouts: [
            readout('Temperature rise', rise, 'K'),
            readout('Model final temperature', initial + rise, '°C'),
          ],
          summary: `The same energy raises this sample by ${display(rise)} K. This assumes no phase change.`,
          working: [
            'Q = m c ΔT',
            'ΔT = Q / (m c)',
            `ΔT = ${energy} / (${mass} × ${capacity}) = ${display(rise)} K`,
          ],
          plot: curve(50000, (q) => initial + q / (mass * capacity)),
          xLabel: 'Supplied energy (J)',
          yLabel: 'Temperature (°C)',
        };
      },
      prediction: {
        question:
          'Double the mass with the same supplied energy. What happens to the temperature rise?',
        options: ['It halves', 'It doubles', 'It stays the same'],
        answer: 0,
        why: 'The same energy is shared by twice the mass: ΔT = Q / (m c).',
      },
      task: 'Calculate the energy needed to raise 0.5 kg of a material with c = 900 J/(kg K) by 20 K. Test it.',
    },
    {
      id: 'phase-change',
      name: 'Melting at a plateau',
      question: 'Where does heating energy go while ice melts at its melting point?',
      controls: [
        control('mass', 'Sample mass', 'kg', 0.1, 3, 1, 0.1),
        control('latent', 'Latent heat of fusion', 'J/kg', 100000, 500000, 334000, 1000),
        control('power', 'Heater power', 'W', 50, 2000, 500, 50),
        control('time', 'Heating time', 's', 0, 3000, 300, 10),
      ],
      examples: [
        {
          id: 'melting',
          name: 'Ice starts at 0°C',
          values: {},
          note: 'Energy changes the phase while temperature stays at the melting point.',
        },
        {
          id: 'complete',
          name: 'Just fully melted',
          values: { time: 668 },
          note: '1 kg at 334 kJ/kg needs 334 kJ to melt.',
        },
        {
          id: 'more-power',
          name: 'More heater power',
          values: { power: 1000 },
          note: 'More power melts the sample faster.',
        },
      ],
      assumptions:
        'Sample starts at its melting point. No heat loss. This model stops counting melting energy after all solid melts. Extra energy is shown separately; subsequent liquid heating is not modelled. Ice preset: 0°C, rounded L = 334000 J/kg.',
      source: 'https://openstax.org/books/university-physics-volume-2/pages/1-5-phase-changes',
      calculate: ({ mass, latent, power, time }) => {
        const supplied = power * time;
        const required = mass * latent;
        const used = Math.min(required, supplied);
        return {
          readouts: [
            readout('Melted fraction', (100 * used) / required, '%'),
            readout('Energy used to melt', used, 'J'),
            readout('Energy beyond melting model', Math.max(0, supplied - required), 'J'),
            readout('Time to melt completely', required / power, 's'),
          ],
          summary:
            supplied < required
              ? 'During melting, energy changes the phase instead of raising temperature.'
              : 'All solid has melted. Further liquid heating is outside this model.',
          working: [
            'Qmelt = m L',
            `Qmelt = ${mass} × ${latent} = ${display(required)} J`,
            'Qsupplied = P t',
            `Qsupplied = ${power} × ${time} = ${display(supplied)} J`,
          ],
          plot: curve(3000, (t) => (100 * Math.min(required, power * t)) / required),
          xLabel: 'Time (s)',
          yLabel: 'Melted fraction (%)',
        };
      },
      prediction: {
        question: 'Ice at its melting point absorbs energy while some solid remains. What happens?',
        options: [
          'More ice melts at the same temperature',
          'Temperature must rise',
          'Energy disappears',
        ],
        answer: 0,
        why: 'Latent heat changes the phase. It does not raise temperature during the plateau.',
      },
      task: 'Predict how doubling both mass and heater power changes the time to melt. Compare both cases.',
    },
    {
      id: 'conduction',
      name: 'Conduction through a slab',
      question:
        'How can two walls with the same temperature difference transfer different amounts of heat?',
      controls: [
        control('conductivity', 'Thermal conductivity', 'W/(m K)', 0.02, 2, 0.04, 0.02),
        control('area', 'Slab area', 'm²', 0.1, 10, 1, 0.1),
        control('thickness', 'Slab thickness', 'm', 0.01, 0.5, 0.1, 0.01),
        control('difference', 'Hot minus cold temperature', 'K', -40, 40, 20),
        control('time', 'Elapsed time', 's', 0, 3600, 600, 60),
      ],
      examples: [
        {
          id: 'insulator',
          name: 'Insulating slab',
          values: {},
          note: 'Lower conductivity reduces transfer rate.',
        },
        {
          id: 'thin',
          name: 'Half the thickness',
          values: { thickness: 0.05 },
          note: 'A shorter conduction path increases transfer rate.',
        },
        {
          id: 'reversed',
          name: 'Reverse temperatures',
          values: { difference: -20 },
          note: 'Reversing the temperature difference reverses heat flow.',
        },
      ],
      assumptions:
        'Steady one-dimensional conduction through a uniform slab. Both face temperatures are held fixed. Positive transfer runs from the first face to the second. No convection, radiation or contact resistance.',
      source:
        'https://openstax.org/books/university-physics-volume-2/pages/1-6-mechanisms-of-heat-transfer',
      calculate: ({ conductivity, area, thickness, difference, time }) => {
        const rate = (conductivity * area * difference) / thickness;
        return {
          readouts: [
            readout('Signed transfer rate', rate, 'W'),
            readout('Transferred energy', rate * time, 'J'),
          ],
          summary:
            rate === 0
              ? 'Equal face temperatures produce no net conduction.'
              : 'Heat flows from the hotter face towards the colder face.',
          working: [
            'P = k A ΔT / d',
            `P = ${conductivity} × ${area} × ${difference} / ${thickness} = ${display(rate)} W`,
            `Q = P t = ${display(rate * time)} J`,
          ],
          plot: curve(3600, (t) => rate * t),
          xLabel: 'Time (s)',
          yLabel: 'Signed energy (J)',
        };
      },
      prediction: {
        question:
          'Double slab thickness with everything else fixed. How does the heat transfer rate change?',
        options: ['It halves', 'It doubles', 'It stays the same'],
        answer: 0,
        why: 'Steady conduction rate is inversely proportional to thickness.',
      },
      task: 'Find two different conductivity and thickness pairs that give the same transfer rate. Explain why.',
    },
  ],
};
